// src/pages/funnels/TemplateGallery.jsx
// Industry template picker rendered inside the "New Funnel" modal.
// Templates come from /api/funnels?resource=templates (system rows seeded
// by lib/funnel-templates via action=sync_templates).
import { useState, useEffect } from 'react';
import { authFetch } from '../../lib/authFetch';
import { cn } from '../../lib/utils';
import { Layers, Loader2 } from 'lucide-react';

const INDUSTRIES = [
  { key: 'all', label: 'All' },
  { key: 'lead-gen', label: 'Lead Gen' },
  { key: 'ecommerce', label: 'E-commerce' },
  { key: 'saas', label: 'SaaS' },
  { key: 'coaching', label: 'Coaching' },
  { key: 'local-business', label: 'Local Business' },
  { key: 'affiliate', label: 'Affiliate' },
];

export default function TemplateGallery({ onSelect, selectedKey }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [industry, setIndustry] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch('/api/funnels?resource=templates');
        const data = await res.json();
        if (!cancelled && data.success) setTemplates(data.templates || []);
      } catch (e) {
        console.error('Template load error:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = industry === 'all'
    ? templates
    : templates.filter(t => t.industry === industry);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5">
        {INDUSTRIES.map(i => (
          <button
            key={i.key}
            type="button"
            onClick={() => setIndustry(i.key)}
            className={cn(
              'px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
              industry === i.key
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-accent'
            )}
          >
            {i.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-10 text-sm text-muted-foreground">
          No templates in this category yet.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[340px] overflow-y-auto pr-1">
          {filtered.map(t => (
            <button
              key={t.template_key}
              type="button"
              onClick={() => onSelect(t)}
              className={cn(
                'text-left rounded-lg border overflow-hidden transition-all hover:shadow-md',
                selectedKey === t.template_key
                  ? 'border-primary ring-2 ring-primary/30'
                  : 'border-border'
              )}
            >
              <div className="aspect-[4/3] bg-muted flex items-center justify-center overflow-hidden">
                {t.thumbnail_url ? (
                  <img src={t.thumbnail_url} alt={t.name} className="w-full h-full object-cover object-top" />
                ) : (
                  <Layers className="h-8 w-8 text-muted-foreground/40" />
                )}
              </div>
              <div className="p-2.5">
                <p className="text-sm font-medium truncate">{t.name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {t.steps_count} step{t.steps_count === 1 ? '' : 's'}
                  {t.description ? ` · ${t.description}` : ''}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
